import type { Ref } from 'react';
import classNames from 'classnames';

import CloseLine24 from '@/assets/icons/close_line_24.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import styles from './RemovableChip.module.scss';

export interface RemovableChipProps {
    /** The chip's value. Long values end in an ellipsis; the whole value is its title. */
    children: string;
    /** Set before the value in secondary text, such as a filter's "Action:". */
    prefix?: string;
    /** The remove button's name, already translated, naming what goes: "Remove filter Action: Delete". */
    removeLabel: string;
    onRemove: () => void;
    /** Receives the remove button, so a list can move focus when a chip goes. */
    removeRef?: Ref<HTMLButtonElement>;
    disabled?: boolean;
    /** On the value, for a type the value needs, such as monospace for ids. */
    valueClassName?: string;
    /** `li` for a chip in a list, which the chips usually are. */
    as?: 'li' | 'span';
}

/**
 * A value with a button that removes it: an active filter, an entry in a list of values.
 * The small button's height and radius, on the form input's background and border.
 */
export default function RemovableChip(props: RemovableChipProps) {
    const {
        children,
        prefix,
        removeLabel,
        onRemove,
        removeRef,
        disabled = false,
        valueClassName,
    } = props;
    const Tag = props.as ?? 'li';

    return (
        <Tag className={styles.chip}>
            {prefix !== undefined && <span className={styles.prefix}>{prefix}</span>}
            {prefix !== undefined && ' '}
            <span className={classNames(styles.value, valueClassName)} title={children}>
                {children}
            </span>
            <button
                ref={removeRef}
                type="button"
                className={styles.remove}
                aria-label={removeLabel}
                title={removeLabel}
                disabled={disabled}
                onClick={onRemove}>
                <SvgIcon
                    icon={CloseLine24}
                    size="custom"
                    customSize={12}
                    color="currentColor"
                    svgProps={{ 'aria-hidden': true }}
                />
            </button>
        </Tag>
    );
}

export { RemovableChip };
